import { Component, OnInit, ViewChild } from '@angular/core';
import { DataTableDirective } from 'angular-datatables';
import { finalize, Subject } from 'rxjs';
import { helper } from 'src/environments/helper';
import { PermissionsService } from 'src/app/services/permissions.service';
import { FormControl, FormGroup } from '@angular/forms';
import { MatCalendar } from '@angular/material/datepicker';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { HttpClient } from '@angular/common/http';
import { NotificariService } from './notificari.service';
import { environment } from 'src/environments/environment';
import Swal from 'sweetalert2';
import { DataTablesResponse } from 'src/app/interfaces/data-table-response.interface';
import { AppFilesService } from 'src/app/services/app-files.service';
import { NotificationService } from 'src/app/services/notification.service';

@Component({
    selector: 'app-notificari',
    templateUrl: './notificari.component.html',
    styleUrls: ['./notificari.component.scss']
})
export class NotificariComponent implements OnInit {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    @ViewChild(PopoverDirective) popover!: PopoverDirective;
    helper = helper;

    errorTitle: string = environment.config.customNotifications.headers.error;
    errorIcon: string = environment.config.customNotifications.icons.error;
    errorType: string = environment.config.customNotifications.icons.error;

    isLoading = false;
    showCard = false;

    // Popover dates

    @ViewChild('startCalendar') startCalendar!: MatCalendar<Date>;
    @ViewChild('endCalendar') endCalendar!: MatCalendar<Date>;

    today: Date = new Date();
    tomorrow: Date = new Date();
    selectedStartDate: Date = new Date(new Date().getFullYear(), 0, 1);
    private _selectedEndDate: Date = new Date();
    selectedFilteringButton: string = 'current_year';

    setLastXDays(numberOfDays: number): void {
        const date = new Date();
        date.setDate(this.today.getDate() - numberOfDays);
        this.selectedStartDate = date;
        this.updateCalendarView(this.startCalendar, date);

        this.selectedEndDate = new Date(this.today);
        this.updateCalendarView(this.endCalendar, this.selectedEndDate);

        this.rerenderDT();
        this.selectedFilteringButton = `last_${numberOfDays}_days`;
    }

    setYear(yearsBack: number): void {
        const year = this.today.getFullYear() - yearsBack;
        this.selectedStartDate = new Date(year, 0, 1); // 1 Ianuarie
        this.updateCalendarView(this.startCalendar, this.selectedStartDate);
        this.selectedEndDate = yearsBack === 0 ? this.today : new Date(year, 11, 31);
        this.updateCalendarView(this.endCalendar, this.selectedEndDate);
        this.rerenderDT();
        this.selectedFilteringButton =
            yearsBack === 0 ? 'current_year' : 'previous_year';
    }

    updateCalendarView(calendar: MatCalendar<Date>, date: Date): void {
        if (calendar) {
            calendar.activeDate = date;
        }
    }

    // Methods used to dinamically select the start date according to the end date

    get selectedEndDate(): Date {
        return this._selectedEndDate;
    }

    set selectedEndDate(date: Date) {
        this._selectedEndDate = date;
        if (this.selectedStartDate > date) {
            this.selectedStartDate = new Date(date);
        }
    }

    closePopover(): void {
        if (this.popover) {
            this.popover.hide();
        }
    }

    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInstance!: DataTables.Api;
    dtParameters: any = {};

    searchParamsForm!: FormGroup;

    initialFormValues = {
        numarCerere: null,
        numarCerereIntern: null,
        email: '',
    };

    buildSearchParamsForm() {
        this.searchParamsForm = new FormGroup({
            numarCerere: new FormControl(null),
            numarCerereIntern: new FormControl(null),
            email: new FormControl(''),
        });
    }

    resetForm() {
        this.searchParamsForm.reset(this.initialFormValues);

        this.rerenderDT();
        this.showCard = true;
    }

    applyFilters(): void {
        this.rerenderDT(true);
    }

    toggleCard() {
        this.showCard = !this.showCard;
    }

    dtLink = environment.interop.basePath + environment.interop.autorizatiiComerciale.notificari.email.getEmailDt;

    selectedRowIds: number[] = [];

    rowCount = 0;
    checkboxSelections = false;

    constructor(
        private http: HttpClient,
        private notificariService: NotificariService,
        public permissionsService: PermissionsService,
        private appFilesService: AppFilesService,
        private notificationService: NotificationService
    ) { }

    ngOnInit(): void {
        this.renderDT();
        this.buildSearchParamsForm();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();
    }

    ngAfterViewInit(): void {
        this.dtTrigger.next('');
    }

    formatDateRender(date: Date) {
        let d = new Date(date),
            month = '' + (d.getMonth() + 1),
            day = '' + d.getDate(),
            year = d.getFullYear();

        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;

        return [year, month, day].join('-');
    }

    renderDT() {
        const self = this;
        this.isLoading = true;

        this.dtOptions = {
            order: [[1, 'desc']],
            serverSide: true,
            searching: false,
            responsive: true,
            pageLength: 25,
            ajax: (dataTablesParameters: any, callback: any) => {
                this.isLoading = true;

                const filters = { ...this.searchParamsForm.value };

                dataTablesParameters.filters = filters;
                dataTablesParameters.date_from = this.formatDateRender(this.selectedStartDate);
                dataTablesParameters.date_to = this.formatDateRender(this.selectedEndDate);

                this.dtParameters = dataTablesParameters;

                this.http
                    .post<DataTablesResponse>(this.dtLink, dataTablesParameters)
                    .subscribe((resp: any) => {
                        this.isLoading = false;
                        this.getTableRowCount();
                        this.checkboxesReset();

                        callback({
                            recordsTotal: resp.data.recordsTotal,
                            recordsFiltered: resp.data.recordsFiltered,
                            data: resp.data.data,
                        });
                    });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: `<input type="checkbox" class="row-checkbox" style="width: 20px; height: 20px; accent-color: #0044cc;" id="headerCheckbox"/>`,
                    data: 'id',
                    sortable: false,
                    className: 'all',
                    render: (data: any, type: any, row: any) => {
                        return `
							<div style="display: flex; align-items: center; gap: 5px;">
								<input type="checkbox" class="row-checkbox" style="width: 20px; height: 20px; accent-color: #0044cc;" value="${row.id}" />
							</div>
						`;
                    },
                },
                {
                    title: 'ID',
                    data: 'id',
                },
                {
                    title: 'Identificare Solicitare',
                    data: 'registration_number',
                    render: (data: any, type: any, row: any) => {
                        return `
                                <div style="display: flex; align-items: center; gap: 5px;">
                                    <span>${row.registration_number}</span>
                                    <span>/</span>
                                    <span>${this.formatDateWithoutHour(row.reg_date)}</span>
                                </div>
                                <div> <br/>
									<span  style="padding: 5px; background-color: #bdbdbd; border: 0px;border-radius:5px">I: ${row.cerere_id}</span>
								</div>
                            `;
                    },
                },
                {
                    title: 'Subiect',
                    data: 'subject',
                },
                {
                    title: 'Trimis către',
                    data: 'email_sent_to',
                    orderable: false,
                },
                {
                    title: 'Status',
                    data: 'email_status',
                    orderable: false,
                },
                {
                    title: 'Dată notificare',
                    data: 'email_date',
                    render: (data: any, type: any, row: any) => {
                        return `
                                <span><i class="fa-solid fa-paper-plane"></i> Dată trimis: ${data ? this.formatDate(data) : 'n/a'}</span>
                                <br>
                                <span><i class="fa-solid fa-gear"></i> Dată inițiere: ${row.created_at ? this.formatDate(row.created_at) : 'n/a'}</span>
                            `;
                    },
                },
                {
                    title: 'Acțiuni',
                    data: null,
                    className: 'all',
                    orderable: false,
                    render: () => {
                        return `
			                <div class="d-flex align-center justify-content-left gap-2">
                                <button style="border: 1px solid #0044cc;" type="button" class="btn btn-light download">
                                    <img src="/assets/icons/view.svg" alt="VIZUALIZAȚI" />
                                    <span style="color: #0044cc; font-weight: 700; font-size: 12px;" class="view-btn-text">VIZUALIZAȚI</span>
                                </button>
                                <button class="action-btn edit resend">
                                    <span style="color: #fff;">RETRIMITE</span>
                                </button>
							</div>
						`;
                    },
                },
            ],
            rowCallback: (row: Node, data: any[] | any) => {
                $('td', row).off('click');

                // Handle click event on the CHECKBOXES
                $(row)
                    .find('.row-checkbox')
                    .off('change')
                    .on('change', function () {
                        let idCerere = Number($(this).val());

                        if ($(this).is(':checked')) {
                            self.selectedRowIds.push(idCerere);
                        } else {
                            self.selectedRowIds = self.selectedRowIds.filter(
                                (id) => id !== idCerere
                            );
                        }

                        if (self.rowCount !== self.selectedRowIds.length) {
                            self.checkHeaderCheckBox(false);
                        } else if (self.rowCount === self.selectedRowIds.length) {
                            self.checkHeaderCheckBox(true);
                        }

                        self.checkboxSelections = self.selectedRowIds.length > 0;
                    });

                const lastTd = (row as HTMLElement).querySelector('td:last-child');
                const downloadButton = lastTd?.querySelector('.download') as HTMLElement;
                const resendButton = lastTd?.querySelector('.resend') as HTMLElement;

                const isActive = data.active === true;

                if (downloadButton) {
                    if (!this.permissionsService.hasPermission(
                        'autcom-module.notificari-email.view'
                    )
                    ) {
                        downloadButton.style.display = 'none';
                    } else {
                        downloadButton.style.display = ''; // Afișăm butonul
                        downloadButton.addEventListener('click', () => {
                            const { id } = data;
                            this.downloadEmail(id);
                        });
                    }
                }

                if (resendButton) {
                    if (!this.permissionsService.hasPermission(
                        'autcom-module.notificari-email.resend'
                    )
                    ) {
                        resendButton.style.display = 'none';
                    } else {
                        resendButton.style.display = ''; // Afișăm butonul
                        resendButton.addEventListener('click', () => {
                            const { id } = data;
                            this.resendEmail(id);
                        });
                    }
                }

                return row;
            },
            headerCallback: (row: Node, data: any[] | any) => {
                const headerCheckbox = $(row).find('#headerCheckbox');

                // Remove any existing event handlers to avoid duplicating them
                headerCheckbox.off('change click');

                // Handle the change event for selecting/deselecting all checkboxes
                headerCheckbox.on('change', function () {
                    if ($(this).is(':checked')) {
                        self.selectedRowIds.length = 0;
                        data.forEach((item: any) => {
                            self.selectedRowIds.push(item.id);
                        });
                        self.checkMultipleCheckboxesByValue(true);
                    } else {
                        self.selectedRowIds.length = 0;
                        self.checkMultipleCheckboxesByValue(false);
                    }
                    self.checkboxSelections = self.selectedRowIds.length > 0;
                });

                // Prevent sorting when clicking the checkbox
                headerCheckbox.on('click', function (e) {
                    e.stopPropagation();
                });

                return row;
            },
        };
    }

    async getTableRowCount() {
        const table = await this.dtElement.dtInstance;
        this.rowCount = table.rows().count();
    }

    checkHeaderCheckBox(checkValue: boolean) {
        const headerCheckbox = document.getElementById(
            'headerCheckbox'
        ) as HTMLInputElement;
        headerCheckbox.checked = checkValue;
    }

    checkMultipleCheckboxesByValue(checkValue: boolean) {
        let checkboxes = document.querySelectorAll<HTMLInputElement>(
            'input[type="checkbox"]'
        );
        checkboxes.forEach((checkbox) => {
            checkbox.checked = checkValue;
        });
    }

    checkboxesReset() {
        this.selectedRowIds.length = 0;
        this.checkMultipleCheckboxesByValue(false);
        this.checkHeaderCheckBox(false);
        this.checkboxSelections = false;
    }

    rerenderDT(paginate: boolean = false): void {
        this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
            dtInstance.draw(paginate);
        });

        this.showCard = false;
    }

    formatDate(input: string): string {
        const date = new Date(input);

        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();

        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = date.getSeconds().toString().padStart(2, '0');

        return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
    }

    formatDateWithoutHour(input: string): string {
        const date = new Date(input);

        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();

        return `${day}.${month}.${year}`;
    }

    downloadEmail(id: number) {
        this.isLoading = true;
        let fullPath = this.notificariService.downloadEmailUrl + id;

        this.appFilesService.downloadFile(fullPath)
            .then(() => {
                this.isLoading = false;
            })
            .catch(err => {
                console.error('Print error: ', err);
                this.isLoading = false;
            })
    }

    resendEmail(id: number) {
        Swal.fire({
            title: helper.dialogConfig.commonTitles.areYouSure,
            text: 'Doriți să retrimiteți email-ul selectat?',
            icon: 'warning',
            showCancelButton: true,
            cancelButtonText: helper.dialogConfig.buttonLabels.close,
            confirmButtonColor: helper.dialogConfig.colorButtons.standard,
            confirmButtonText: helper.dialogConfig.buttonLabels.confirm,
            showLoaderOnConfirm: true,
            reverseButtons: true,
            allowOutsideClick: false,
        }).then((result) => {
            if (result.isConfirmed) {
                this.isLoading = true;
                this.notificariService.resendEmail(id)
                    .pipe(finalize(() => (this.isLoading = false)))
                    .subscribe({
                        next: () => {
                            Swal.fire({
                                title: helper.dialogConfig.headers.success,
                                text: 'Notificare Email trimisă cu succes.',
                                icon: 'success',
                                confirmButtonText: helper.dialogConfig.buttonLabels.close,
                                allowOutsideClick: false,
                            }).then(() => {
                                this.rerenderDT();
                            });
                        },
                        error: (err) => {
                            let msg;
                            try {
                                // try to catch the message from server
                                msg = err.error.errors.message[0];
                            } catch (error) {
                                // controll output message
                                msg = helper.dialogConfig.generalMessages.error;
                            }

                            Swal.fire({
                                title: helper.dialogConfig.headers.errorAtentie,
                                text: msg,
                                icon: 'warning',
                                confirmButtonText: helper.dialogConfig.buttonLabels.retry,
                                showCancelButton: true,
                                cancelButtonText: helper.dialogConfig.buttonLabels.close,
                                reverseButtons: true,
                                allowOutsideClick: false,
                            }).then((result) => {
                                if (result.isConfirmed) {
                                    this.resendEmail(id);
                                }
                            });
                        }
                    })
            }
        })
    }

    printNotificari() {
        this.isLoading = true;
        const payload = { ...this.dtParameters, selectedRowIds: this.selectedRowIds }

        this.notificariService.printNotificariEmail(payload)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (response: any) => {
                    let dataType = response.type;
                    let binaryData = [];
                    binaryData.push(response);
                    let downloadLink = document.createElement('a');
                    downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: dataType }));
                    downloadLink.setAttribute('target', '_blank');
                    document.body.appendChild(downloadLink);
                    downloadLink.click();
                    document.body.removeChild(downloadLink);
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }
}
